import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	IOffer,
	IOfferContext,
	ITypeContactFooterFields,
	ITypeVignettesOrderManagementFields,
	IVignetteOffer,
	OfferContext,
	countryUrlMap,
	getContactFooterCollection,
	getUrlBasePath,
	transformShortTextLabelsToObject,
} from '@adac-mp/shared';
import {
	Button,
	CircleCrossIcon,
	ContentLoader,
	DocumentIcon,
	MailIcon,
	MastercardIcon,
	PayPalIcon,
	Phone,
	Richtext,
	SEPA,
	Typography,
	VisaIcon,
} from '../../index';
import { useFlowId } from '../../hooks/useFlowId';

interface IContactProps {
	withSeparator?: boolean;
}
interface IContactComponentProps {
	isDkvPage?: boolean;
	labels?: ITypeContactFooterFields;
	orderManagementLabel?: ITypeVignettesOrderManagementFields;
	withSeparator: boolean;
	offer?: IOffer | IVignetteOffer;
}

const ContactComponent = ({
	isDkvPage = false,
	labels,
	orderManagementLabel,
	withSeparator = false,
	offer,
}: IContactComponentProps) => {
	const location = useLocation();
	const navigate = useNavigate();
	const paymentMethodIcons = {
		1: <PayPalIcon className="inline align-middle shrink-0" />,
		2: (
			<span>
				<MastercardIcon className="inline align-middle shrink-0" />
				<VisaIcon className="inline align-middle ml-1.5 shrink-0" />
			</span>
		),
		3: <SEPA className="inline align-middle shrink-0" />,
	};

	const renderIcons = () => {
		if (isDkvPage) {
			return offer ? (
				offer.paymentMethods.map(({ id, name }) => <span key={name}>{paymentMethodIcons[id]}</span>)
			) : (
				<SEPA className="inline align-middle shrink-0" />
			);
		}
		return offer ? (
			offer.paymentMethods.map(({ id, name }) => <span key={name}>{paymentMethodIcons[id]}</span>)
		) : (
			<>
				<SEPA className="inline align-middle shrink-0" />{' '}
				<PayPalIcon className="inline align-middle shrink-0" />{' '}
				<MastercardIcon className="inline align-middle shrink-0" />
				<VisaIcon className="inline align-middle shrink-0" />
			</>
		);
	};

	const renderEmailButton = () => (
		<Button
			fullWidth
			className="gap-2 hover:bg-gray-700 md:max-w-[340px]"
			variant="shy"
			href={labels?.linkRightButton || 'mailto:maut-service@adac.de'}
			tracking={{
				contact_click: {
					label: 'Email',
					element: 'Weitere Informationen',
				},
			}}>
			<MailIcon size="20" />
			{labels?.textRightButton || 'maut-service@adac.de'}
		</Button>
	);

	const renderVignetteContactInfo = () => (
		<>
			<Button
				fullWidth
				variant="shy"
				href={labels?.linkLeftButton || 'tel:+498976763355'}
				className="order-0 gap-2 hover:bg-gray-700 md:max-w-[340px]"
				tracking={{
					contact_click: {
						label: 'Telefon',
						element: 'Weitere Informationen',
					},
				}}>
				<Phone size="20" />
				{labels?.textLeftButton || '089 76 76 33 55'}
			</Button>
			{renderEmailButton()}
			<Button
				fullWidth
				variant="shy"
				className="order-1 gap-2 hover:bg-gray-700 md:max-w-[340px]"
				tracking={{
					button_click: {
						label: 'Bestellung verwalten',
						element: 'Weitere Informationen',
					},
				}}
				onClick={() => {
					navigate(getUrlBasePath('/cancel-order/vignette'));
				}}>
				<DocumentIcon size="20" />
				{orderManagementLabel?.headlineIntro || 'Bestellung verwalten'}
			</Button>
		</>
	);

	const renderDkvContactInfo = () => (
		<>
			<Button
				fullWidth
				variant="shy"
				href={labels?.linkLeftButton || 'tel:+498976763355'}
				className="order-0 gap-2 hover:bg-gray-700 md:max-w-[340px]"
				tracking={{
					contact_click: {
						label: 'Telefon',
						element: 'Weitere Informationen',
					},
				}}>
				<Phone size="20" />
				{labels?.textLeftButton || '089 76 76 33 55'}
			</Button>
			{renderEmailButton()}
			<Button
				fullWidth
				variant="shy"
				className="order-1 gap-2 hover:bg-gray-700 md:max-w-[340px]"
				tracking={{
					button_click: {
						label: 'Bankdaten ändern',
						element: 'Weitere Informationen',
					},
				}}
				onClick={() => {
					navigate(getUrlBasePath('/cancel-order/vignette'));
				}}>
				<DocumentIcon size="20" />
				Bankdaten ändern
			</Button>
		</>
	);

	return (
		<section className="bg-white">
			<div
				className={`flex flex-col gap-4 px-1.5 py-5 md:py-6 md:px-4 md:max-w-desktop md:mx-auto ${
					withSeparator ? 'border-t border-solid border-x-0 border-b-0 border-t-gray-warm' : ''
				}`}>
				<Typography className="mx-auto w-fit" component="h3" variant="header" size="xxl" color="title">
					{labels?.headline || 'Weitere Informationen'}
				</Typography>
				{labels?.richtextList && <Richtext checkmarkList document={labels.richtextList} />}
				<div className="flex flex-col items-center gap-3 md:flex-row md:justify-center">
					<Typography>{labels?.paymentMethods || 'Unsere Zahlungsarten:'}</Typography>
					<div className="flex flex-row gap-1.5 justify-center">{renderIcons()}</div>
				</div>
				<div className="flex flex-col items-center gap-1.5 md:flex-row md:justify-center">
					{location.pathname.includes('camper-mautbox')
						? renderDkvContactInfo()
						: renderVignetteContactInfo()}
				</div>
			</div>
		</section>
	);
};

const DKVContact = ({ withSeparator = false }: IContactProps) => {
	const { dkvOffers, lang } = useContext<IOfferContext>(OfferContext);
	const { loading, error, data } = useQuery(getContactFooterCollection, {
		variables: { lang: lang || 'de', slug: 'dkv' },
	});
	const labels = !error ? (data?.contactFooterCollection.items[0] as ITypeContactFooterFields) : undefined;
	const orderManagementLabel =
		!error && data
			? (transformShortTextLabelsToObject(
					data.vignettesOrderManagementCollection.items[0].shortTextLabels.items,
			  ) as ITypeVignettesOrderManagementFields)
			: undefined;

	return loading ? (
		<ContentLoader isLoading hasPortal={false} />
	) : (
		<ContactComponent
			isDkvPage
			withSeparator={withSeparator}
			labels={labels}
			orderManagementLabel={orderManagementLabel}
			offer={dkvOffers[0]}
		/>
	);
};

const VignetteContact = ({ withSeparator = false }: IContactProps) => {
	const flowId = useFlowId();
	const slug = countryUrlMap[flowId || 0];
	const { vignetteOffers, lang } = useContext<IOfferContext>(OfferContext);
	const { loading, error, data } = useQuery(getContactFooterCollection, {
		variables: { lang: lang || 'de', slug },
	});

	const labels = !error ? (data?.contactFooterCollection.items[0] as ITypeContactFooterFields) : undefined;
	const orderManagementLabel =
		!error && data
			? (transformShortTextLabelsToObject(
					data.vignettesOrderManagementCollection.items[0].shortTextLabels.items,
			  ) as ITypeVignettesOrderManagementFields)
			: undefined;

	return loading ? (
		<ContentLoader isLoading hasPortal={false} />
	) : (
		<ContactComponent
			withSeparator={withSeparator}
			labels={labels}
			orderManagementLabel={orderManagementLabel}
			offer={vignetteOffers[0]}
		/>
	);
};

export const Contact = ({ withSeparator = false }: IContactProps) => {
	const location = useLocation();
	const isDkvPage = location.pathname.includes('camper-mautbox');

	return isDkvPage ? <DKVContact withSeparator={withSeparator} /> : <VignetteContact withSeparator={withSeparator} />;
};
